import React, { useState } from 'react';
import { ChevronRight, Award, Eye, Lightbulb, Shield, Users, Leaf, Zap, Globe, ArrowRight, Plus } from 'lucide-react';

const About = () => {
  const [activeTab, setActiveTab] = useState('story');

  const tabContent = {
    story: {
      title: "Our Journey",
      content: (
        <div className="space-y-6">
          <p className="text-lg text-slate-300 leading-relaxed">
            Founded over 15 years ago with a vision to transform Kenya's infrastructure landscape, JTG Enterprises has grown from a small team of passionate engineers to a leading construction powerhouse. Our journey began with a simple belief: that quality engineering can build not just structures, but communities and futures.
          </p>
          <p className="text-slate-300 leading-relaxed">
            From our first water project in rural Kenya to constructing major highways that connect cities, we've consistently pushed the boundaries of what's possible. Today, we stand as a testament to Kenyan ingenuity, having successfully completed over 200 projects across East Africa, each one contributing to the region's growth and development.
          </p>
          <p className="text-slate-300 leading-relaxed">
            Our evolution reflects Kenya's own transformation—we've adapted to new technologies, embraced sustainable practices, and expanded our expertise to meet the changing needs of our rapidly developing nation. Every project tells a story of progress, innovation, and our unwavering commitment to excellence.
          </p>
        </div>
      )
    },
    expertise: {
      title: "Our Expertise",
      content: (
        <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-6">
          {[
            {
              title: "Water Infrastructure",
              description: "From boreholes to treatment plants, we've delivered clean water solutions to over 500,000 people across Kenya.",
              projects: "120+ projects"
            },
            {
              title: "Road Construction",
              description: "Building the highways and roads that connect communities, with over 2,000km of roads constructed.",
              projects: "85+ projects"
            },
            {
              title: "Building Construction",
              description: "Creating structures that stand the test of time, from residential complexes to commercial centers.",
              projects: "150+ projects"
            },
            {
              title: "Engineering Design",
              description: "Innovative design solutions that blend functionality with sustainability and aesthetic appeal.",
              projects: "300+ designs"
            }
          ].map((item, index) => (
            <div key={index} className="bg-slate-800/30 rounded-xl p-6 border border-slate-700/30 hover:border-slate-600/50 transition-all duration-300">
              <h4 className="text-xl font-bold text-white mb-3">{item.title}</h4>
              <p className="text-slate-300 mb-4 text-sm leading-relaxed">{item.description}</p>
              <div className="text-sm text-cyan-400 font-medium">{item.projects}</div>
            </div>
          ))}
        </div>
      )
    },
    impact: {
      title: "Our Impact",
      content: (
        <div className="space-y-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: "💧",
                title: "Water Access",
                stat: "500K+",
                description: "People now have access to clean water through our projects"
              },
              {
                icon: "🏘️",
                title: "Communities Served",
                stat: "200+",
                description: "Rural and urban communities across East Africa"
              },
              {
                icon: "🌱",
                title: "Environmental Projects",
                stat: "50+",
                description: "Sustainable solutions that protect our environment"
              }
            ].map((impact, index) => (
              <div key={index} className="text-center p-4">
                <div className="text-3xl sm:text-4xl mb-3">{impact.icon}</div>
                <div className="text-2xl font-bold text-cyan-400 mb-2">{impact.stat}</div>
                <h4 className="text-lg font-semibold text-white mb-2">{impact.title}</h4>
                <p className="text-sm text-slate-300 leading-relaxed">{impact.description}</p>
              </div>
            ))}
          </div>
          <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-2xl p-6 lg:p-8 border border-cyan-500/20">
            <h4 className="text-xl lg:text-2xl font-bold text-white mb-4">Building Kenya's Future</h4>
            <p className="text-slate-300 leading-relaxed text-sm lg:text-base">
              Every project we undertake is guided by our commitment to sustainable development. We don't just build infrastructure; we create lasting positive change that improves lives, protects the environment, and contributes to Kenya's economic growth. Our work has created thousands of jobs, supported local businesses, and helped establish Kenya as a leader in African infrastructure development.
            </p>
          </div>
        </div>
      )
    }
  };

  const values = [
    {
      icon: Award,
      title: "Excellence",
      description: "We pursue perfection in every detail, from initial design concepts to final project delivery, ensuring world-class standards in all our work.",
      gradient: "from-yellow-400 to-orange-500"
    },
    {
      icon: Shield,
      title: "Safety First",
      description: "Zero compromise on safety. We implement rigorous safety protocols to protect our team, clients, and communities in every project.",
      gradient: "from-cyan-400 to-blue-500"
    },
    {
      icon: Award,
      title: "Integrity",
      description: "Unwavering ethical standards and transparency in all our dealings, building trust through honest communication and reliable delivery.",
      gradient: "from-green-400 to-emerald-500"
    },
    {
      icon: Lightbulb,
      title: "Innovation",
      description: "Embracing cutting-edge technologies and creative solutions to tackle complex engineering challenges and exceed client expectations.",
      gradient: "from-purple-400 to-pink-500"
    },
    {
      icon: Leaf,
      title: "Sustainability",
      description: "Committed to environmental stewardship, implementing eco-friendly practices and creating solutions that benefit future generations.",
      gradient: "from-blue-400 to-indigo-500"
    },
    {
      icon: Users,
      title: "Collaboration",
      description: "Fostering strong partnerships with clients, communities, and stakeholders to achieve shared goals and create lasting positive impact.",
      gradient: "from-red-400 to-pink-500"
    }
  ];

  return (
    <section id="about" className="relative min-h-screen bg-slate-950 overflow-hidden">
      {/* Enhanced Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Gradient overlays */}
        <div className="absolute top-0 left-1/4 w-64 h-64 lg:w-96 lg:h-96 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-1/3 right-1/4 w-56 h-56 lg:w-80 lg:h-80 bg-gradient-to-br from-purple-500/15 to-pink-500/15 rounded-full blur-3xl animate-float-delayed" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 lg:w-72 lg:h-72 bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-full blur-3xl animate-float" />
        
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(59,130,246,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.05)_1px,transparent_1px)] bg-[size:40px_40px] opacity-30" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        
        {/* Section Header */}
        <div className="text-center mb-16 lg:mb-20 animate-fade-in">
          <div className="inline-flex items-center gap-2 bg-cyan-500/10 backdrop-blur-sm border border-cyan-500/20 rounded-full px-3 py-2 text-xs sm:text-sm font-medium text-cyan-400 mb-6">
            <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse" />
            About JTG Enterprises
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-6 leading-tight px-4">
            Engineering Excellence,
            <span className="block bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 bg-clip-text text-transparent">
              Transforming Kenya
            </span>
          </h2>
          
          <p className="text-lg sm:text-xl text-slate-300 max-w-4xl mx-auto leading-relaxed px-4">
            For over 15 years, JTG Enterprises has been at the forefront of Kenya's infrastructure revolution. We're not just builders—we're visionaries who understand that every road, every water system, and every structure we create shapes the future of our communities and our nation.
          </p>
        </div>

        {/* Mission & Vision - Enhanced */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mb-16 lg:mb-20">
          
          {/* Mission Card */}
          <div className="group relative animate-slide-up">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-all duration-500" />
            <div className="relative bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 lg:p-8 hover:border-cyan-500/30 transition-all duration-500 h-full">
              <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
                <div className="flex-shrink-0 mx-auto sm:mx-0">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-2xl flex items-center justify-center shadow-lg shadow-cyan-500/25">
                    <Award className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                  </div>
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="text-xl lg:text-2xl font-bold text-white mb-4">Our Mission</h3>
                  <p className="text-slate-300 leading-relaxed mb-4 text-sm lg:text-base">
                    To deliver world-class engineering solutions that transform communities, enhance lives, and drive sustainable development across East Africa. We are committed to excellence in every project, building lasting relationships with our clients, and contributing meaningfully to the societies we serve.
                  </p>
                  <p className="text-slate-400 text-xs lg:text-sm">
                    Through innovation, integrity, and unwavering dedication to quality, we minimize environmental impact while maximizing positive social and economic outcomes.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Vision Card */}
          <div className="group relative animate-slide-up-delayed">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-all duration-500" />
            <div className="relative bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 lg:p-8 hover:border-purple-500/30 transition-all duration-500 h-full">
              <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
                <div className="flex-shrink-0 mx-auto sm:mx-0">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-purple-400 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/25">
                    <Eye className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                  </div>
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="text-xl lg:text-2xl font-bold text-white mb-4">Our Vision</h3>
                  <p className="text-slate-300 leading-relaxed mb-4 text-sm lg:text-base">
                    To be East Africa's most trusted and innovative engineering partner, recognized for our technical excellence, sustainable practices, and transformative impact on regional development. We envision a future where our engineering solutions serve as catalysts for economic growth and improved quality of life.
                  </p>
                  <p className="text-slate-400 text-xs lg:text-sm">
                    Leading the way in sustainable infrastructure development while setting new standards for engineering excellence across the region.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Core Values Section - Enhanced */}
        <div className="mb-16 lg:mb-20 animate-fade-in-delayed">
          <div className="text-center mb-12">
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">Our Core Values</h3>
            <p className="text-slate-400 max-w-2xl mx-auto px-4 text-sm lg:text-base">
              These fundamental principles drive our decision-making, shape our culture, and guide our approach to every project we undertake
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <div key={index} className="group relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${value.gradient} opacity-0 group-hover:opacity-10 rounded-2xl blur transition-all duration-500`} />
                  <div className="relative bg-slate-900/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-4 lg:p-6 text-center hover:border-slate-600/50 transition-all duration-300 h-full">
                    <div className={`w-12 h-12 lg:w-16 lg:h-16 bg-gradient-to-br ${value.gradient} rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                      <IconComponent className="w-6 h-6 lg:w-8 lg:h-8 text-white" />
                    </div>
                    <h4 className="text-lg lg:text-xl font-bold text-white mb-3">{value.title}</h4>
                    <p className="text-slate-300 text-xs lg:text-sm leading-relaxed">{value.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Interactive Tabs Section */}
        <div className="mb-16 lg:mb-20 animate-fade-in-delayed">
          <div className="bg-slate-900/30 backdrop-blur-sm border border-slate-700/50 rounded-3xl p-6 lg:p-8 xl:p-12">
            
            {/* Tab Navigation */}
            <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-8">
              {Object.keys(tabContent).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 sm:px-6 sm:py-3 rounded-xl font-medium transition-all duration-300 text-sm sm:text-base ${
                    activeTab === tab
                      ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/25'
                      : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700/50 hover:text-white'
                  }`}
                >
                  {tabContent[tab].title}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="min-h-[300px] lg:min-h-[400px]">
              <h3 className="text-2xl lg:text-3xl font-bold text-white mb-6 text-center">
                {tabContent[activeTab].title}
              </h3>
              {tabContent[activeTab].content}
            </div>
          </div>
        </div>

        {/* Enhanced Stats and CTA Section */}
        <div className="bg-slate-900/30 backdrop-blur-sm border border-slate-700/50 rounded-3xl p-6 lg:p-8 xl:p-12 animate-fade-in-delayed">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-2xl lg:text-3xl font-bold text-white mb-6">Proven Track Record</h3>
            <p className="text-base lg:text-lg text-slate-300 leading-relaxed mb-8 px-4">
              Our numbers tell the story of our commitment to excellence and our impact on Kenya's development. Every statistic represents lives improved, communities connected, and futures built.
            </p>
            
            {/* Enhanced Stats Row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-12">
              {[
                { number: "200+", label: "Projects Completed", description: "Across Kenya & East Africa" },
                { number: "15+", label: "Years of Excellence", description: "Building Kenya's future" },
                { number: "50+", label: "Expert Professionals", description: "Engineers & specialists" },
                { number: "500K+", label: "Lives Impacted", description: "Through our water projects" }
              ].map((stat, index) => (
                <div key={index} className="text-center group p-2">
                  <div className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent mb-2 group-hover:scale-110 transition-transform duration-300">
                    {stat.number}
                  </div>
                  <div className="text-xs sm:text-sm font-semibold text-white uppercase tracking-wider mb-1">{stat.label}</div>
                  <div className="text-xs text-slate-400 leading-tight">{stat.description}</div>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center px-4">
              <a href="#portfolio" className="flex-1 sm:flex-none">
                <button className="w-full sm:w-auto group relative px-6 lg:px-8 py-3 lg:py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-cyan-500/25 hover:-translate-y-1">
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <span className="relative flex items-center justify-center gap-2 text-sm lg:text-base">
                    Explore Our Portfolio
                    <ArrowRight className="w-4 h-4 lg:w-5 lg:h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </span>
                </button>
              </a>
              
              <a href="#contact" className="flex-1 sm:flex-none">
                <button className="w-full sm:w-auto group relative px-6 lg:px-8 py-3 lg:py-4 bg-transparent border-2 border-slate-600 text-white font-semibold rounded-xl overflow-hidden transition-all duration-300 hover:border-cyan-500 hover:shadow-lg hover:shadow-cyan-500/20">
                  <span className="relative flex items-center justify-center gap-2 text-sm lg:text-base">
                    Start Your Project
                    <Plus className="w-4 h-4 lg:w-5 lg:h-5 group-hover:rotate-45 transition-transform duration-300" />
                  </span>
                </button>
              </a>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-30px) rotate(-5deg); }
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-float {
          animation: float 8s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 10s ease-in-out infinite 2s;
        }
        .animate-fade-in {
          animation: fade-in 0.8s ease-out;
        }
        .animate-fade-in-delayed {
          animation: fade-in 0.8s ease-out 0.4s both;
        }
        .animate-slide-up {
          animation: slide-up 0.8s ease-out 0.2s both;
        }
        .animate-slide-up-delayed {
          animation: slide-up 0.8s ease-out 0.4s both;
        }
      `}</style>
    </section>
  );
};

export default About;